// ============================================
// BASE ROLE TEMPLATE ENUM
// ============================================
export enum BaseRoleTemplate {
  MANAGER = "MANAGER",
  EMPLOYEE = "EMPLOYEE",
  GATE_KEEPER = "GATE_KEEPER",
}

// ============================================
// PERMISSION ENUM
// ============================================
export enum Permission {
  // Company
  VIEW_COMPANY_DETAILS = "VIEW_COMPANY_DETAILS",
  UPDATE_COMPANY_DETAILS = "UPDATE_COMPANY_DETAILS",

  // User Management
  CREATE_USER = "CREATE_USER",
  VIEW_USERS = "VIEW_USERS",
  UPDATE_USER = "UPDATE_USER",
  DELETE_USER = "DELETE_USER",
  MANAGE_VENUE_PERMISSIONS = "MANAGE_VENUE_PERMISSIONS",

  // Venue
  CREATE_VENUE = "CREATE_VENUE",
  VIEW_VENUES = "VIEW_VENUES",
  UPDATE_VENUE = "UPDATE_VENUE",
  DELETE_VENUE = "DELETE_VENUE",

  // Event
  CREATE_EVENT = "CREATE_EVENT",
  VIEW_EVENTS = "VIEW_EVENTS",
  UPDATE_EVENT = "UPDATE_EVENT",
  DELETE_EVENT = "DELETE_EVENT",

  // Guest List
  CREATE_GUEST_LIST = "CREATE_GUEST_LIST",
  VIEW_GUEST_LISTS = "VIEW_GUEST_LISTS",
  UPDATE_GUEST_LIST = "UPDATE_GUEST_LIST",
  DELETE_GUEST_LIST = "DELETE_GUEST_LIST",
  MANAGE_GUEST_LIST_PERMISSIONS = "MANAGE_GUEST_LIST_PERMISSIONS",
  SET_GUEST_LIMITS = "SET_GUEST_LIMITS",

  // Guest
  CREATE_GUEST = "CREATE_GUEST",
  VIEW_GUESTS = "VIEW_GUESTS",
  UPDATE_GUEST = "UPDATE_GUEST",
  DELETE_GUEST = "DELETE_GUEST",
  CHECK_IN_GUEST = "CHECK_IN_GUEST",
  CHECK_OUT_GUEST = "CHECK_OUT_GUEST",
  MANAGE_BLACKLIST = "MANAGE_BLACKLIST",

  // Invitation
  CREATE_INVITATION = "CREATE_INVITATION",
  VIEW_INVITATIONS = "VIEW_INVITATIONS",
  DELETE_INVITATION = "DELETE_INVITATION",
  SEND_INVITATION_EMAIL = "SEND_INVITATION_EMAIL",

  // Statistics & Profile
  VIEW_STATISTICS = "VIEW_STATISTICS",
  /** PR yalnızca kendi hak edişini görür; mekan toplamı ve diğer PR'lar kapalı. */
  VIEW_OWN_EARNINGS = "VIEW_OWN_EARNINGS",
  VIEW_OWN_PROFILE = "VIEW_OWN_PROFILE",
  UPDATE_OWN_PROFILE = "UPDATE_OWN_PROFILE",
}

// ============================================
// CUSTOM ROLE TYPES
// ============================================
export interface CustomRolePermission {
  id: string;
  customRoleId: string;
  permission: Permission;
  createdAt: string;
}

export interface CustomRole {
  id: string;
  name: string;
  description: string | null;
  companyId: string;
  isActive: boolean;
  isTemplate: boolean;
  baseRole: BaseRoleTemplate | null;
  createdAt: string;
  updatedAt: string;
  permissions: CustomRolePermission[];
  _count?: { users: number };
}

export interface RoleTemplate {
  role: string;
  permissions: Permission[];
}

// ============================================
// DTOs
// ============================================
export interface CreateRoleFromTemplateDto {
  name: string;
  description?: string;
  baseRole: BaseRoleTemplate;
  addPermissions?: Permission[];
  removePermissions?: Permission[];
}

export interface CreateCustomRoleDto {
  name: string;
  description?: string;
  permissions: Permission[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  isActive?: boolean;
  permissions?: Permission[];
}

// ============================================
// UI HELPERS
// ============================================
export const PERMISSION_CATEGORIES = {
  COMPANY: {
    label: "Şirket ve Rapor",
    permissions: [
      Permission.VIEW_COMPANY_DETAILS,
      Permission.UPDATE_COMPANY_DETAILS,
      Permission.VIEW_STATISTICS,
      Permission.VIEW_OWN_EARNINGS,
    ],
  },
  USER: {
    label: "Kullanıcı Yönetimi",
    permissions: [
      Permission.CREATE_USER,
      Permission.VIEW_USERS,
      Permission.UPDATE_USER,
      Permission.DELETE_USER,
      Permission.MANAGE_VENUE_PERMISSIONS,
    ],
  },
  VENUE: {
    label: "Mekan",
    permissions: [
      Permission.CREATE_VENUE,
      Permission.VIEW_VENUES,
      Permission.UPDATE_VENUE,
      Permission.DELETE_VENUE,
    ],
  },
  EVENT: {
    label: "Etkinlik",
    permissions: [
      Permission.CREATE_EVENT,
      Permission.VIEW_EVENTS,
      Permission.UPDATE_EVENT,
      Permission.DELETE_EVENT,
    ],
  },
  GUEST_LIST: {
    label: "Misafir Listesi",
    permissions: [
      Permission.CREATE_GUEST_LIST,
      Permission.VIEW_GUEST_LISTS,
      Permission.UPDATE_GUEST_LIST,
      Permission.DELETE_GUEST_LIST,
      Permission.MANAGE_GUEST_LIST_PERMISSIONS,
      Permission.SET_GUEST_LIMITS,
    ],
  },
  GUEST: {
    label: "Misafir",
    permissions: [
      Permission.CREATE_GUEST,
      Permission.VIEW_GUESTS,
      Permission.UPDATE_GUEST,
      Permission.DELETE_GUEST,
      Permission.CHECK_IN_GUEST,
      Permission.CHECK_OUT_GUEST,
      Permission.MANAGE_BLACKLIST,
    ],
  },
  INVITATION: {
    label: "Davetiye",
    permissions: [
      Permission.CREATE_INVITATION,
      Permission.VIEW_INVITATIONS,
      Permission.DELETE_INVITATION,
      Permission.SEND_INVITATION_EMAIL,
    ],
  },
  OTHER: {
    label: "Diğer",
    permissions: [Permission.VIEW_OWN_PROFILE, Permission.UPDATE_OWN_PROFILE],
  },
} as const;

export const PERMISSION_LABELS: Record<Permission, string> = {
  [Permission.VIEW_COMPANY_DETAILS]: "Şirket bilgilerini gör",
  [Permission.UPDATE_COMPANY_DETAILS]: "Şirket bilgilerini düzenle",
  [Permission.CREATE_USER]: "Kullanıcı oluştur",
  [Permission.VIEW_USERS]: "Kullanıcıları gör",
  [Permission.UPDATE_USER]: "Kullanıcı düzenle",
  [Permission.DELETE_USER]: "Kullanıcı sil",
  [Permission.MANAGE_VENUE_PERMISSIONS]: "Mekan izinlerini yönet",
  [Permission.CREATE_VENUE]: "Mekan oluştur",
  [Permission.VIEW_VENUES]: "Mekanları gör",
  [Permission.UPDATE_VENUE]: "Mekan düzenle",
  [Permission.DELETE_VENUE]: "Mekan sil",
  [Permission.CREATE_EVENT]: "Etkinlik oluştur",
  [Permission.VIEW_EVENTS]: "Etkinlikleri gör",
  [Permission.UPDATE_EVENT]: "Etkinlik düzenle",
  [Permission.DELETE_EVENT]: "Etkinlik sil",
  [Permission.CREATE_GUEST_LIST]: "Liste oluştur",
  [Permission.VIEW_GUEST_LISTS]: "Listeleri gör",
  [Permission.UPDATE_GUEST_LIST]: "Liste düzenle",
  [Permission.DELETE_GUEST_LIST]: "Liste sil",
  [Permission.MANAGE_GUEST_LIST_PERMISSIONS]: "Liste izinlerini yönet",
  [Permission.SET_GUEST_LIMITS]: "Misafir limitleri belirle",
  [Permission.CREATE_GUEST]: "Misafir oluştur",
  [Permission.VIEW_GUESTS]: "Misafirleri gör",
  [Permission.UPDATE_GUEST]: "Misafir düzenle",
  [Permission.DELETE_GUEST]: "Misafir sil",
  [Permission.CHECK_IN_GUEST]: "Giriş yap",
  [Permission.CHECK_OUT_GUEST]: "Çıkış yap",
  [Permission.MANAGE_BLACKLIST]: "Kara listeyi yönet",
  [Permission.CREATE_INVITATION]: "Davetiye oluştur",
  [Permission.VIEW_INVITATIONS]: "Davetiyeleri gör",
  [Permission.DELETE_INVITATION]: "Davetiye sil",
  [Permission.SEND_INVITATION_EMAIL]: "Davetiye e-postası gönder",
  [Permission.VIEW_STATISTICS]: "İstatistikleri gör",
  [Permission.VIEW_OWN_EARNINGS]: "Kendi hak edişini gör",
  [Permission.VIEW_OWN_PROFILE]: "Kendi profilini gör",
  [Permission.UPDATE_OWN_PROFILE]: "Kendi profilini düzenle",
};

export const TEMPLATE_LABELS: Record<BaseRoleTemplate, string> = {
  [BaseRoleTemplate.MANAGER]: "Yönetici",
  [BaseRoleTemplate.EMPLOYEE]: "Çalışan",
  [BaseRoleTemplate.GATE_KEEPER]: "Kapı Görevlisi",
};

export const TEMPLATE_DESCRIPTIONS: Record<BaseRoleTemplate, string> = {
  [BaseRoleTemplate.MANAGER]:
    "Event ve guest yönetimi, sınırlı yönetim yetkileri",
  [BaseRoleTemplate.EMPLOYEE]: "Temel guest işlemleri ve görüntüleme",
  [BaseRoleTemplate.GATE_KEEPER]: "Sadece check-in/out işlemleri",
};
